/**
 * Connection management constants
 */

import { getGoogleDriveDataAccessSummary } from './legal/googleDriveDataAccess';
import type { NangoProvider, ProviderDisplayConfig } from '../types/connections';

/**
 * Provider display configurations
 */
export const PROVIDER_CONFIGS: Record<NangoProvider, ProviderDisplayConfig> = {
  'google-drive': {
    provider: 'google-drive',
    displayName: 'Google Drive',
    icon: 'google-drive',
    brandColor: '#4285F4',
    description: 'Export to Drive and pick folders you choose—connected only to your account.',
    dataAccessSummary: getGoogleDriveDataAccessSummary(),
  },
  'one-drive': {
    provider: 'one-drive',
    displayName: 'OneDrive',
    icon: 'onedrive',
    brandColor: '#0078D4',
    description: 'Connect your Microsoft OneDrive to sync and manage files',
  },
  dropbox: {
    provider: 'dropbox',
    displayName: 'Dropbox',
    icon: 'dropbox',
    brandColor: '#0061FF',
    description: 'Connect your Dropbox to sync and manage files',
  },
  hubspot: {
    provider: 'hubspot',
    displayName: 'HubSpot',
    icon: 'hubspot',
    brandColor: '#0078D4',
    description: 'Connect your HubSpot to sync and manage files',
  },
  snowflake: {
    provider: 'snowflake',
    displayName: 'Snowflake',
    icon: 'snowflake',
    brandColor: '#29B5E8',
    description: 'Connect your Snowflake data warehouse to sync and manage data',
  },
  servicenow: {
    provider: 'servicenow',
    displayName: 'ServiceNow',
    icon: 'servicenow',
    brandColor: '#81B5A1',
    description: 'Connect your ServiceNow instance to export and manage records',
  },
  confluence: {
    provider: 'confluence',
    displayName: 'Confluence',
    icon: 'confluence',
    brandColor: '#0052CC',
    description: 'Connect your Confluence instance to export and manage documents',
  },
  'google-cloud-storage': {
    provider: 'google-cloud-storage',
    displayName: 'Google Cloud Storage',
    icon: 'google-cloud-storage',
    brandColor: '#4285F4',
    description: 'Connect your Google Cloud Storage to export and manage files',
  },
  zoho: {
    provider: 'zoho',
    displayName: 'Zoho CRM',
    icon: 'zoho',
    brandColor: '#E42536',
    description: 'Connect your Zoho CRM to export form data as CRM records',
  },
  notion: {
    provider: 'notion',
    displayName: 'Notion',
    icon: 'notion',
    brandColor: '#000000',
    description: 'Connect your Notion workspace to export and manage documents',
  },
  slack: {
    provider: 'slack',
    displayName: 'Slack',
    icon: 'slack',
    brandColor: '#4A154B',
    description: 'Connect your Slack workspace to export documents and messages',
  },
  box: {
    provider: 'box',
    displayName: 'Box',
    icon: 'box',
    brandColor: '#0061D5',
    description: 'Connect your Box account to sync and manage files',
  },
  signnow: {
    provider: 'signnow',
    displayName: 'SignNow',
    icon: 'signnow',
    brandColor: '#1A73E8',
    description: 'Connect SignNow for e-signature workflows',
  },
  quickbooks: {
    provider: 'quickbooks',
    displayName: 'QuickBooks',
    icon: 'quickbooks',
    brandColor: '#2CA01C',
    description: 'Connect QuickBooks to export accounting data',
  },
  trello: {
    provider: 'trello',
    displayName: 'Trello',
    icon: 'trello',
    brandColor: '#0079BF',
    description: 'Connect your Trello boards for export workflows',
  },
};

/**
 * Connection status display configurations
 */
export const CONNECTION_STATUS_CONFIG = {
  connected: {
    label: 'Connected',
    color: 'green',
    icon: 'check-circle',
  },
  disconnected: {
    label: 'Disconnected',
    color: 'gray',
    icon: 'x-circle',
  },
  error: {
    label: 'Error',
    color: 'red',
    icon: 'alert-circle',
  },
  expired: {
    label: 'Expired',
    color: 'yellow',
    icon: 'clock',
  },
} as const;

/**
 * Default OAuth scopes for each provider
 */
export const DEFAULT_PROVIDER_SCOPES: Record<NangoProvider, string[]> = {
  'google-drive': [
    'https://www.googleapis.com/auth/drive.file',
    'https://www.googleapis.com/auth/drive.readonly',
  ],
  'one-drive': ['Files.ReadWrite', 'Files.Read'],
  dropbox: ['files.content.read', 'files.content.write'],
  hubspot: ['contacts.read', 'contacts.write', 'crm.objects.notes.write'],
  snowflake: [], // Snowflake uses different authentication mechanism
  servicenow: ['useraccount', 'table.*.write', 'table.*.read'],
  confluence: ['confluence.content.readonly', 'confluence.content.write'],
  'google-cloud-storage': [
    'https://www.googleapis.com/auth/devstorage.read_write',
    'https://www.googleapis.com/auth/cloud-platform',
  ],
  zoho: [
    'ZohoCRM.modules.ALL', // Full access to create/update/read records in all modules (Contacts, Leads, Accounts, Deals, etc.)
    'ZohoCRM.settings.profiles.READ', // Required for accessing profile-related settings
  ],
  notion: [], // Scopes are managed in the Notion UI during authorization
  slack: ['channels:read', 'chat:write', 'files:write'],
  box: ['root_readwrite'],
  signnow: [],
  quickbooks: ['com.intuit.quickbooks.accounting'],
  trello: ['read', 'write'],
};

/**
 * Connection operation timeout in milliseconds
 */
export const CONNECTION_OPERATION_TIMEOUT = 30000; // 30 seconds

/**
 * Connection status refresh interval in milliseconds
 */
export const CONNECTION_STATUS_REFRESH_INTERVAL = 60000; // 1 minute

/**
 * Maximum retry attempts for failed operations
 */
export const MAX_RETRY_ATTEMPTS = 3;

/**
 * Base retry delay in milliseconds (for exponential backoff)
 */
export const BASE_RETRY_DELAY = 1000; // 1 second

/**
 * Connection health check interval in milliseconds
 */
export const HEALTH_CHECK_INTERVAL = 300000; // 5 minutes

/**
 * Error codes for connection operations
 */
export const CONNECTION_ERROR_CODES = {
  // Network errors
  NETWORK_ERROR: 'NETWORK_ERROR',
  TIMEOUT: 'TIMEOUT',

  // Authentication errors
  UNAUTHORIZED: 'UNAUTHORIZED',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',

  // Permission errors
  INSUFFICIENT_PERMISSIONS: 'INSUFFICIENT_PERMISSIONS',
  SCOPE_MISMATCH: 'SCOPE_MISMATCH',

  // Provider errors
  PROVIDER_ERROR: 'PROVIDER_ERROR',
  PROVIDER_UNAVAILABLE: 'PROVIDER_UNAVAILABLE',
  RATE_LIMITED: 'RATE_LIMITED',

  // Validation errors
  INVALID_CONNECTION_ID: 'INVALID_CONNECTION_ID',
  CONNECTION_NOT_FOUND: 'CONNECTION_NOT_FOUND',
  ALREADY_CONNECTED: 'ALREADY_CONNECTED',

  // System errors
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  DATABASE_ERROR: 'DATABASE_ERROR',
  CONFIGURATION_ERROR: 'CONFIGURATION_ERROR',
} as const;

/**
 * User-friendly error messages for connection error codes
 */
export const CONNECTION_ERROR_MESSAGES: Record<string, string> = {
  [CONNECTION_ERROR_CODES.NETWORK_ERROR]:
    'Network connection failed. Please check your internet connection and try again.',
  [CONNECTION_ERROR_CODES.TIMEOUT]: 'The operation timed out. Please try again.',
  [CONNECTION_ERROR_CODES.UNAUTHORIZED]: 'Authentication failed. Please reconnect your account.',
  [CONNECTION_ERROR_CODES.TOKEN_EXPIRED]:
    'Your connection has expired. Please reconnect your account.',
  [CONNECTION_ERROR_CODES.INVALID_CREDENTIALS]:
    'Invalid credentials. Please reconnect your account.',
  [CONNECTION_ERROR_CODES.INSUFFICIENT_PERMISSIONS]:
    'Insufficient permissions. Please reconnect with the required permissions.',
  [CONNECTION_ERROR_CODES.SCOPE_MISMATCH]: 'Permission mismatch. Please reconnect your account.',
  [CONNECTION_ERROR_CODES.PROVIDER_ERROR]:
    'The service provider encountered an error. Please try again later.',
  [CONNECTION_ERROR_CODES.PROVIDER_UNAVAILABLE]:
    'The service is temporarily unavailable. Please try again later.',
  [CONNECTION_ERROR_CODES.RATE_LIMITED]: 'Too many requests. Please wait a moment and try again.',
  [CONNECTION_ERROR_CODES.INVALID_CONNECTION_ID]:
    'Invalid connection. Please refresh the page and try again.',
  [CONNECTION_ERROR_CODES.CONNECTION_NOT_FOUND]: 'Connection not found. Please refresh the page.',
  [CONNECTION_ERROR_CODES.ALREADY_CONNECTED]: 'This service is already connected.',
  [CONNECTION_ERROR_CODES.INTERNAL_ERROR]: 'An internal error occurred. Please try again later.',
  [CONNECTION_ERROR_CODES.DATABASE_ERROR]: 'Database error. Please try again later.',
  [CONNECTION_ERROR_CODES.CONFIGURATION_ERROR]: 'Configuration error. Please contact support.',
};

/**
 * User closed the Nango OAuth window before finishing — intentional, not an app failure.
 */
export function isUserCancelledNangoConnect(message: string | null | undefined): boolean {
  if (!message) return false;
  const m = message.toLowerCase();
  return (
    m.includes('user closed the connect window') ||
    m.includes('closed the connect window before completing')
  );
}
